import React, { useContext, useEffect, useReducer } from 'react';
import { PageNames, UserInfo } from '../../Constants/Common';
import UserAccountReducer from './UserAccountReducer';
import UserDetailForm from './UserDetailForm';
import { CommonValidation } from '../common/commonValidation/commonValidation';
import LoadingContainer from '../common/loadingContainer/LoadingContainer';
import { DashboardData } from '../common/CommonTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { SiteContext } from '../../context/SiteContext';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { VehicleContext } from '../../context/VehicleContext';
import Dashboard from './Dashboard';
import { DashboardApi } from '../../api/DashboardApi';
import { CustomerContext } from '../../context/CustomerContext';
import { t } from 'i18next';

export type UserDetails = {
    postcode: string;
    surname: string;
};

export type UserAccountState = {
    userDetails: UserDetails;
    searchCompleted: boolean;
    searchingForVehicles: boolean;
    loading: boolean;
};

const HomePage = (): JSX.Element => {
    const { user, getAccessTokenSilently } = useAuth0();
    const { brandId } = useContext(SiteContext);

    const initialState: UserAccountState = {
        userDetails: {
            surname: '',
            postcode: ''
        },
        searchCompleted: false,
        searchingForVehicles: false,
        loading: true
    };

    const [state, dispatch] = useReducer(UserAccountReducer, initialState);

    const { vehicles, setVehicles } = useContext(VehicleContext);
    const { setCustomer } = useContext(CustomerContext);

    const { userDetails, searchCompleted, searchingForVehicles, loading } =
        state;

    const isValidUserDetails = (surname: string, postcode: string): boolean => {
        if (
            CommonValidation.isNotEmpty(surname) &&
            CommonValidation.isNotEmpty(postcode)
        ) {
            return true;
        }

        return false;
    };

    useEffect(() => {
        if (brandId === 0) return;

        const userKey = `${user?.sub || ''}_${brandId}`;
        const surname =
            localStorage.getItem(`${userKey}_${UserInfo.Surname}`) || '';
        const postcode =
            localStorage.getItem(`${userKey}_${UserInfo.Postcode}`) || '';

        if (isValidUserDetails(surname, postcode)) {
            updateUserDetails(postcode, surname);
        } else {
            dispatch({
                type: 'set-loading',
                loading: false
            });
        }
    }, [brandId]);

    useEffect(() => {
        const controller = new AbortController();

        const loadDashboardData = async () => {
            try {
                const token = await getAccessTokenSilently();

                const successCallback = (dashboardData: DashboardData) => {
                    dispatch({ type: 'set-search-vehicles-completed' });
                    setVehicles(dashboardData.vehicles);
                    setCustomer(dashboardData.customer);
                    saveUserDetailsInLocalStorage();
                    GoogleAnalytics.setPageView(PageNames.Vehicle);
                };

                const failureCallback = () => {
                    dispatch({
                        type: 'set-loading',
                        loading: false
                    });
                };

                new DashboardApi().getDashboardData(
                    userDetails.surname,
                    userDetails.postcode,
                    user?.email || '',
                    brandId,
                    token,
                    successCallback,
                    failureCallback,
                    controller.signal
                );
            } catch (e) {
                console.log(e.message);
            }
        };

        if (isValidUserDetails(userDetails.surname, userDetails.postcode)) {
            dispatch({
                type: 'set-loading',
                loading: true
            });

            loadDashboardData();
        }

        return () => {
            controller.abort();
        };
    }, [userDetails]);

    const updateUserDetails = (postcode: string, surname: string) => {
        dispatch({
            type: 'update-user-detail',
            postcode,
            surname
        });
    };

    const saveUserDetailsInLocalStorage = () => {
        const userKey = `${user?.sub || ''}_${brandId.toString()}`;

        localStorage.setItem(
            `${userKey}_${UserInfo.Surname}`,
            userDetails.surname
        );
        localStorage.setItem(
            `${userKey}_${UserInfo.Postcode}`,
            userDetails.postcode
        );
    };

    const getDisplay = (): JSX.Element => {
        if (!searchCompleted && vehicles.length <= 0) {
            return (
                <UserDetailForm
                    initialPostcode={userDetails.postcode}
                    initialSurname={userDetails.surname}
                    loading={searchingForVehicles}
                    onUserDetailsSubmitted={updateUserDetails}
                />
            );
        } else {
            //Vehicle code moved to VehiclesPage.tsx
            return <Dashboard />;
        }
    };

    return (
        <LoadingContainer loading={loading} message={t('loading')}>
            {getDisplay()}
        </LoadingContainer>
    );
};

export default HomePage;
